// src/components/auth/components/MiniEstOLSLM.tsx
import React, { useState } from "react";
import styled from "styled-components";
import { Typography, InputNumber, Button } from "antd";
import { Link } from "react-router-dom";

const { Text } = Typography;

// Type Definitions
interface AgeGroup {
  ageRange: string;
  min: number;
  max: number;
  minutes: number;
}

// Styled Components for MiniEstOLSLM
const MiniContainer = styled.div`
  padding: 15px;
  background: #1a1a1a; /* Dark background to match LoginMonitor */
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 300px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.7);
  }

  &:focus-within {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 10px;
`;

const MiniTitle = styled(Text)`
  color: #ffa500; /* Orange to match aOLSLM Assets */
  font-size: 18px;
  font-weight: 600;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
`;

const ResultText = styled(Text)`
  color: #ffffff;
  font-size: 16px;
`;

const StyledButton = styled(Button)`
  background: ${(props) => (props.type === "primary" ? "#3552fc" : "#ff8c00")};
  color: ${(props) => (props.type === "primary" ? "#ffffff" : "#000000")};
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 14px;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: ${(props) => (props.type === "primary" ? "#2a43d6" : "#e07b00")};
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }
`;

const StyledInputNumber = styled(InputNumber)`
  .ant-input-number-input {
    background: #333;
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 4px;
  }

  &:hover,
  &:focus {
    .ant-input-number-input {
      border-color: #ffd700;
    }
  }
`;

const MiniEstOLSLM: React.FC = () => {
  // State for user age and total savings
  const [userAge, setUserAge] = useState<number>(0);
  const [totalSavings, setTotalSavings] = useState<number | null>(null);

  // Predefined age group averages
  const ageGroupAverages: AgeGroup[] = [
    { ageRange: "0-20", min: 0, max: 20, minutes: 6 },
    { ageRange: "20-30", min: 20, max: 30, minutes: 5.5 },
    { ageRange: "30-40", min: 30, max: 40, minutes: 5 },
    { ageRange: "40-50", min: 40, max: 50, minutes: 4 },
    { ageRange: "50-60", min: 50, max: 60, minutes: 3 },
    { ageRange: "60-70", min: 60, max: 70, minutes: 2 },
    { ageRange: "70-80", min: 70, max: 80, minutes: 1 },
    { ageRange: "80-90", min: 80, max: 90, minutes: 0.8 },
    { ageRange: "90-100", min: 90, max: 100, minutes: 0.5 },
    { ageRange: "100+", min: 100, max: Infinity, minutes: 0.3 },
  ];

  // Function to get the adjusted daily average based on age group with linear interpolation
  const getAdjustedDailyAverage = (age: number): number => {
    // Find the relevant age group
    let selectedGroup: AgeGroup | null = null;
    let nextGroup: AgeGroup | null = null;
    for (let i = 0; i < ageGroupAverages.length; i++) {
      const group = ageGroupAverages[i];
      if (age >= group.min && age < group.max) {
        selectedGroup = group;
        nextGroup = ageGroupAverages[i + 1] || selectedGroup; // Use the same group if it's the last one
        break;
      }
    }

    if (!selectedGroup) return 5; // Fallback to a default value if no group is found

    // Linear interpolation between the current and next age group
    const { min: minAge, minutes: minMinutes } = selectedGroup;
    const { max: maxAge, minutes: maxMinutes } = nextGroup;

    if (age >= maxAge) return minMinutes; // If age exceeds the max of the last group

    const ageRange = maxAge - minAge;
    const ageProgress = (age - minAge) / ageRange;
    const interpolatedMinutes = minMinutes + (maxMinutes - minMinutes) * ageProgress;

    return interpolatedMinutes;
  };

  // Function to calculate total Inner Equity OLS Light Minutes over 100 years
  const calculateTotalSavings = (): void => {
    const lifeExpectancy = 100; // Fixed at 100 years
    let currentAge = userAge;
    let remainingYears = lifeExpectancy - userAge;
    let cumulativeLightMinutes = 0;

    // Ensure remainingYears is valid
    if (remainingYears <= 0) {
      console.log("Invalid remaining years:", remainingYears);
      setTotalSavings(null);
      return;
    }

    for (let year = 0; year <= remainingYears; year++) {
      const adjustedDailyAverage = getAdjustedDailyAverage(currentAge);
      const yearlyLightMinutes = adjustedDailyAverage * 366; // Minutes per year
      cumulativeLightMinutes += yearlyLightMinutes;
      currentAge++;
    }

    setTotalSavings(Number(cumulativeLightMinutes.toFixed(2)));
  };

  return (
    <MiniContainer>
      <TitleWrapper>
        <MiniTitle></MiniTitle>
        <StyledButton type="primary">
          <Link
            to="/OLSStudentProspect"
            style={{ color: "#ffffff", textDecoration: "none" }}
            aria-label="Navigate to OLS Student Prospect page"
          >
            OLSStudentProspect
          </Link>
        </StyledButton>
      </TitleWrapper>
      <InputWrapper>
        <Text style={{ color: "#ffffff" }}>Your Age:</Text>
        <StyledInputNumber
          min={0}
          max={150}
          value={userAge}
          onChange={(value) => setUserAge(value as number)}
          style={{ width: 80 }}
          aria-label="Enter your age"
        />
        <StyledButton onClick={calculateTotalSavings} aria-label="Calculate total light savings">
          Calculate
        </StyledButton>
      </InputWrapper>
      {totalSavings !== null && (
        <ResultText>
          Total Inner Equity OLS Light Minutes (100 years): {totalSavings}
        </ResultText>
      )}
    </MiniContainer>
  );
};

export default React.memo(MiniEstOLSLM);